import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from 'context/CartContext';
import { useStore } from 'context/StoreContext';
import Price, { discountPercent } from './Price';
import ResponsiveImage from './ResponsiveImage';
import { PlusIcon } from './icons';

const DEFAULT_SIZES = '(min-width: 1280px) 300px, (min-width: 1024px) 23vw, (min-width: 640px) 31vw, 46vw';

export function Badge({ tone = 'plain', children }) {
  const tones = {
    plain: 'bg-background/95 text-ink',
    sale: 'bg-sale text-white',
    muted: 'bg-ink/75 text-white',
  };
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-1 text-[11px] font-semibold uppercase leading-none tracking-[0.08em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Product tile for grids and rails. Layout follows theme.layout.productCard:
 * "minimal" (image + text, no box) or "framed" (bordered card).
 * Single-variant products get a quick add button; others link to the page.
 */
export default function ProductCard({ product, sizes = DEFAULT_SIZES, priority = false, as: Heading = 'h3' }) {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { add } = useCart();
  const [hovered, setHovered] = useState(false);
  const framed = theme.layout.productCard === 'framed';
  const href = `/produk/${product.slug}`;

  const variants = product.variants || [];
  const quick = variants.length === 1 && variants[0].stock > 0 ? variants[0] : null;
  const swatches = variants.filter((v) => v.swatch);
  const discount = discountPercent(product.priceAmount, product.compareAtAmount);
  const optionsText = swatches.length
    ? t('product.colorCount', { count: swatches.length })
    : variants.length > 1
      ? variants.map((v) => v.name).join(' · ')
      : variants[0] && variants[0].name;

  return (
    <article
      className={`group relative flex h-full flex-col ${framed ? 'border border-border bg-surface' : ''}`}
      onMouseEnter={() => setHovered(true)}
    >
      <div
        className={`relative aspect-[4/5] overflow-hidden bg-surface-alt ${framed ? '' : 'rounded'}`}
      >
        <ResponsiveImage
          image={product.image}
          sizes={sizes}
          priority={priority}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out [@media(hover:hover)]:group-hover:scale-[1.03]"
        />
        {product.hoverImage && hovered && (
          <ResponsiveImage
            image={product.hoverImage}
            sizes={sizes}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 [@media(hover:hover)]:group-hover:opacity-100"
          />
        )}
        <div className="pointer-events-none absolute left-2.5 top-2.5 flex flex-col items-start gap-1.5">
          {!product.available && <Badge tone="muted">{t('product.soldOut')}</Badge>}
          {product.available && discount > 0 && (
            <Badge tone="sale">{t('product.save', { percent: discount })}</Badge>
          )}
          {product.isNew && <Badge>{t('product.new')}</Badge>}
        </div>
        {quick && (
          <button
            type="button"
            onClick={() => add(product, quick, 1)}
            className="absolute bottom-2.5 right-2.5 z-10 inline-flex h-10 w-10 items-center justify-center rounded-btn bg-background/95 text-ink shadow-sm transition hover:bg-primary hover:text-primary-contrast [@media(hover:hover)]:translate-y-1 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-focus-within:translate-y-0 [@media(hover:hover)]:group-focus-within:opacity-100 [@media(hover:hover)]:group-hover:translate-y-0 [@media(hover:hover)]:group-hover:opacity-100"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="sr-only">{t('product.quickAdd', { name: product.name })}</span>
          </button>
        )}
      </div>

      <div className={`flex flex-1 flex-col ${framed ? 'p-3 sm:p-4' : 'pt-3'}`}>
        <Heading
          className={
            framed
              ? 'font-display text-[15px] leading-snug text-ink'
              : 'text-[15px] font-medium leading-snug text-ink'
          }
        >
          {/* Stretched link: the whole card is clickable, the button stays on top. */}
          <Link to={href} className="outline-none after:absolute after:inset-0 after:content-[''] focus-visible:underline">
            {product.name}
          </Link>
        </Heading>
        {product.subtitle && (
          <p className="mt-1 line-clamp-1 text-[13px] text-muted">{product.subtitle}</p>
        )}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pt-2">
          <Price amount={product.priceAmount} compareAt={product.compareAtAmount} from={product.priceVaries} />
          {swatches.length > 0 ? (
            <span className="flex items-center gap-1" aria-label={optionsText}>
              {swatches.slice(0, 5).map((v) => (
                <span
                  key={v.id}
                  aria-hidden="true"
                  className={`h-3 w-3 rounded-full ring-1 ring-inset ring-black/10 ${v.stock > 0 ? '' : 'opacity-40'}`}
                  style={{ backgroundColor: v.swatch }}
                />
              ))}
            </span>
          ) : (
            optionsText && <span className="text-[12px] text-muted">{optionsText}</span>
          )}
        </div>
      </div>
    </article>
  );
}

/** Same footprint as a card, for loading states. */
export function ProductCardSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="aspect-[4/5] rounded bg-surface-alt motion-safe:animate-pulse" />
      <div className="mt-3 h-4 w-3/4 rounded-sm bg-surface-alt" />
      <div className="mt-2 h-3 w-1/2 rounded-sm bg-surface-alt" />
      <div className="mt-3 h-4 w-1/3 rounded-sm bg-surface-alt" />
    </div>
  );
}
